/**
 * Primary Sidebar - Expandable Module Navigation
 * Icon-only when collapsed (56px) or icon+text when expanded (200px)
 * Dark Truvex brand theme with smooth transitions
 */

import React from 'react';
import { Layout, Menu, Tooltip, Dropdown, Avatar, Space } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ExperimentOutlined,
  SafetyOutlined,
  FileTextOutlined,
  SettingOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { spacing, colors } from '../../theme/designTokens';
import { useUser } from '../../contexts/UserContext';

const { Sider } = Layout;

/**
 * Logo Symbol - shown when collapsed (takes full width of 56px)
 */
const LogoSymbol = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="20.712504459240563 -67.85499572753906 532.2988480798219 708.8601684570312"
    style={{
      width: '52px',
      height: '52px',
      display: 'block',
    }}
  >
    <g transform="rotate(0 286.86192849915153 286.57508850097656)">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        x="90.71250445924056"
        y="2.1450042724609375"
        viewBox="1824.5299072265625 520.52001953125 1350.9500732421875 1958.969970703125"
        height="568.8601684570312"
        width="392.29884807982194"
        preserveAspectRatio="xMinYMin"
      >
        <path d="M2500,2479.49c35.06-649.72,34.78-1309.25-0.01-1958.97C2465.21,1170.24,2464.93,1829.77,2500,2479.49z" fill="#FFFAEA" />
        <path d="M2646.7,1842.86l0.13,14.75c1.78,151.74,6.99,303.48,25.18,454.33c17.55-145.89,23.02-292.54,24.93-439.2c34.72-18.7,69.19-37.9,103.53-57.49v-54.69c-47.44,24.17-94.25,49.35-140.55,74.92L2646.7,1842.86z" fill="#FFFAEA" />
        <path d="M2865.34,1557.31c0-0.02,0-0.05,0-0.07c105.85-49.99,210.76-102.76,310.14-160.71c-103.91,32.33-208.19,71.39-311.03,112.98c0-0.01,0-0.02,0-0.03c-1.91-83.95-5.72-167.9-11.57-251.59c-5.98,86.87-10.05,174-11.83,261.12c-48.42,19.8-96.43,40.09-143.93,60.48c-1.52-193.19-6.07-392.72-25.12-583.7c-21.78,217.14-24.31,443.27-25.78,661.71c64.19-28.79,129.18-58.1,193.94-88.38c0,0,0,0.01,0,0.01c-0.76,57.11-0.64,114.22,0.51,171.2c0.25,17.04,0.64,33.96,1.14,50.88c2.16,75.43,5.85,150.85,11.07,226.15c5.6-79.75,9.29-159.63,11.32-239.63c72.63-43.37,143.47-89.67,210.89-140.17c-71.23,26.58-141.19,57.36-209.87,90.69C2866.23,1671.28,2866.23,1614.29,2865.34,1557.31z" fill="#FFFAEA" />
        <path d="M2302.76,1579.44c-32.43-13.99-65.25-27.98-98.32-41.72v52.02c49.86,23.15,99.85,45.66,149.32,67.79c-1.4-218.39-3.94-444.67-25.82-661.79C2309,1186.92,2304.29,1386.24,2302.76,1579.44z" fill="#FFFAEA" />
        <path d="M2204.44,1763.11v54.95c32.69,18.7,65.63,36.89,98.57,54.69c1.91,146.65,7.38,293.31,24.93,439.2c18.32-150.85,23.53-302.59,25.18-454.33l0.13-14.75l-13.23-7.38C2295.26,1810.81,2250.11,1786.51,2204.44,1763.11z" fill="#FFFAEA" />
        <path d="M2159.04,1519.03c-1.91-87.13-5.85-174.26-11.83-261.13c-5.85,83.82-9.67,167.77-11.57,251.72c-102.9-41.72-207.33-80.77-311.11-113.08c99.47,58,204.4,110.79,310.22,160.77c-0.89,56.98-0.89,113.96,0.13,170.95c-68.81-33.32-138.64-64.11-210-90.69c67.41,50.5,138.39,96.92,211.01,140.29c2.04,79.88,5.72,159.75,11.32,239.51c5.22-75.3,8.9-150.72,10.94-226.15c0.51-16.92,0.89-33.83,1.27-50.75c1.02-57.11,1.14-114.35,0.38-171.46C2159.67,1552.35,2159.42,1535.69,2159.04,1519.03z" fill="#FFFAEA" />
      </svg>
    </g>
  </svg>
);

/**
 * Logo Vertical - symbol + TRUVEX text, shown when expanded
 */
const LogoVertical = () => (
  <img
    src="/logos/vertical.svg"
    alt="Truvex"
    style={{
      width: '140px',
      height: 'auto',
      display: 'block',
    }}
  />
);

/**
 * Module navigation items
 */
const getModuleItems = () => [
  {
    key: '/dashboard',
    icon: <DashboardOutlined />,
    label: 'Dashboard',
    title: 'Dashboard',
  },
  {
    key: '/ai-ml',
    icon: <ExperimentOutlined />,
    label: 'AI/ML',
    title: 'AI/ML',
  },
  {
    key: '/quality-compliance',
    icon: <SafetyOutlined />,
    label: 'Quality & Compliance',
    title: 'Quality & Compliance',
  },
  {
    key: '/library',
    icon: <BookOutlined />,
    label: 'Library',
    title: 'Library',
  },
  {
    key: '/trustgrid',
    icon: <FileTextOutlined />,
    label: 'TrustGrid',
    title: 'TrustGrid',
  },
  {
    key: '/administration',
    icon: <SettingOutlined />,
    label: 'Administration',
    title: 'Administration',
  },
];

function PrimarySidebar({ onModuleClick, expanded, setExpanded }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logoutUser } = useUser();

  // Determine selected module from current path
  const getSelectedModule = () => {
    const path = location.pathname;
    if (path.startsWith('/ai-ml')) return '/ai-ml';
    if (path.startsWith('/quality-compliance')) return '/quality-compliance';
    if (path.startsWith('/library')) return '/library';
    if (path.startsWith('/trustgrid')) return '/trustgrid';
    if (path.startsWith('/administration')) return '/administration';
    return '/dashboard';
  };

  const selectedKey = getSelectedModule();

  const handleMenuClick = ({ key }) => {
    // Notify parent to toggle secondary sidebar OR navigate
    if (onModuleClick) {
      onModuleClick(key);
    } else {
      navigate(key);
    }
  };

  const handleLogout = () => {
    logoutUser();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: (
        <div>
          <div style={{ fontWeight: 600 }}>
            {user?.first_name && user?.last_name
              ? `${user.first_name} ${user.last_name}`
              : user?.username || 'User'}
          </div>
          {user?.email && (
            <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
              {user.email}
            </div>
          )}
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  return (
    <div
      style={{
        width: expanded ? 200 : 56,
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        overflow: 'hidden',
        boxShadow: 'none',
        zIndex: 50,
        backgroundColor: colors.sidebar.primary,
        borderRight: `1px solid ${colors.sidebar.divider}`,
        transition: 'width 0.3s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Logo section - starts from top */}
      <div
        style={{
          height: '140px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          padding: expanded ? '20px' : '2px',
          transition: 'padding 0.3s ease-in-out',
        }}
      >
        {expanded ? <LogoVertical /> : <LogoSymbol />}
      </div>

      {/* Divider line below logo */}
      <div
        style={{
          height: '1px',
          backgroundColor: colors.sidebar.divider,
          flexShrink: 0,
          marginBottom: '20px',
        }}
      />

      {/* Scrollable navigation area - grows to fill space */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
        }}
        className="primary-sidebar-scroll"
      >
        {/* Module Navigation */}
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          onClick={handleMenuClick}
          theme="dark"
          inlineCollapsed={!expanded}
          style={{
            borderRight: 0,
            backgroundColor: 'transparent',
            width: '100%',
          }}
          className="primary-rail-menu"
          items={getModuleItems()}
        />
      </div>

      {/* Toggle button */}
      <div
        style={{
          borderTop: `1px solid ${colors.sidebar.divider}`,
          backgroundColor: 'transparent',
          flexShrink: 0,
          padding: '12px',
          display: 'flex',
          justifyContent: expanded ? 'flex-end' : 'center',
        }}
      >
        <div
          onClick={() => {
            console.log('Toggle clicked, current expanded:', expanded);
            setExpanded(!expanded);
          }}
          style={{
            width: expanded ? 'auto' : '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: colors.brand.ivory,
            fontSize: '16px',
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            padding: expanded ? '0 12px' : '0',
            gap: expanded ? '8px' : 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.interactive.hoverDark;
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          {expanded ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
          {expanded && <span style={{ fontSize: '13px', fontWeight: 500 }}>Collapse</span>}
        </div>
      </div>

      {/* User menu */}
      <div
        style={{
          borderTop: `1px solid ${colors.sidebar.divider}`,
          backgroundColor: 'transparent',
          flexShrink: 0,
        }}
      >
        <Dropdown
          menu={{ items: userMenuItems }}
          placement="topRight"
          trigger={['click']}
        >
          <div
            style={{
              padding: expanded ? `${spacing.md}px` : '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: expanded ? spacing.sm : 0,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.interactive.hoverDark;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Avatar
              icon={<UserOutlined />}
              size={36}
              style={{
                flexShrink: 0,
                backgroundColor: colors.sidebar.primaryActive,
                color: colors.brand.ivory,
                transition: 'background-color 0.2s ease',
              }}
              className="profile-avatar"
            />
            {expanded && (
              <span style={{ color: colors.brand.ivory, fontSize: 14, fontWeight: 500 }}>
                {user?.username || 'User'}
              </span>
            )}
          </div>
        </Dropdown>
      </div>
    </div>
  );
}

export default PrimarySidebar;
